import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { IdentificationCard, Wallet, Bank, Lock, ArrowSquareOut, Check, ShieldCheck, FileText, Trash } from "@phosphor-icons/react";

export default function Identity() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [apaarList, setApaarList] = useState([]);
  const [apaarForm, setApaarForm] = useState({ student_id: "", name: "", dob: "", aadhaar_last4: "" });
  const [kyc, setKyc] = useState({ student_id: "", aadhaar_last4: "", consent: false, txn_id: "", otp: "", status: "" });
  const [dlForm, setDlForm] = useState({ student_id: "", documents: "10th_marksheet,12th_marksheet,aadhaar" });
  const [docs, setDocs] = useState([]);
  const [consents, setConsents] = useState([]);

  const loadOverview = useCallback(async () => {
    const { data } = await api.get("/identity/overview");
    setOverview(data);
  }, []);
  const loadApaar = useCallback(async () => {
    const { data } = await api.get("/identity/apaar");
    setApaarList(data);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (tab === "apaar") loadApaar(); }, [tab, loadApaar]);

  const run = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post("/identity/bulk", { action });
      toast.success(data.message); await loadOverview();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const issueApaar = async () => {
    if (!apaarForm.student_id || !apaarForm.name || !apaarForm.dob) return toast.error("All required");
    try {
      const { data } = await api.post("/identity/apaar/issue", apaarForm);
      toast.success(data.already_issued ? `Already issued: ${data.record.apaar_id}` : `APAAR ID ${data.apaar_id} issued`);
      setApaarForm({ student_id: "", name: "", dob: "", aadhaar_last4: "" });
      loadApaar();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const startKyc = async () => {
    if (!kyc.consent) return toast.error("Consent required");
    try {
      const { data } = await api.post("/identity/aadhaar/start", {
        aadhaar_last4: kyc.aadhaar_last4, student_id: kyc.student_id, consent: true,
      });
      setKyc(k => ({ ...k, txn_id: data.txn_id, status: data.status }));
      toast.success(`OTP sent — txn ${data.txn_id}. Use 123456 (mock).`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const verifyKyc = async () => {
    try {
      const { data } = await api.post("/identity/aadhaar/verify", { txn_id: kyc.txn_id, otp: kyc.otp });
      setKyc(k => ({ ...k, status: data.status }));
      toast.success("Verified");
    } catch (err) { toast.error(err?.response?.data?.detail || "OTP invalid"); }
  };

  const pullDigiLocker = async () => {
    if (!dlForm.student_id) return toast.error("Student ID required");
    try {
      const docs = dlForm.documents.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/identity/digilocker/pull", { student_id: dlForm.student_id, documents: docs });
      toast.success(`${data.count} documents pulled from DigiLocker`);
      const r = await api.get(`/identity/documents/${dlForm.student_id}`);
      setDocs(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const loadConsents = async () => {
    if (!dlForm.student_id) return toast.error("Enter student ID");
    try { const { data } = await api.get(`/identity/consents/${dlForm.student_id}`); setConsents(data); }
    catch { toast.error("Failed"); }
  };

  const withdrawConsent = async (id) => {
    try { await api.delete(`/identity/consents/${id}`); toast.success("Withdrawn"); loadConsents(); }
    catch { toast.error("Failed"); }
  };

  if (!overview) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  const kpis = [
    { I: IdentificationCard, label: "APAAR IDs Issued", v: overview.apaar_issued.toLocaleString() },
    { I: Wallet, label: "DigiLocker Verified", v: overview.digilocker_verified.toLocaleString() },
    { I: Bank, label: "ABC Credits Synced", v: overview.abc_credits_pushed.toLocaleString() },
    { I: Lock, label: "Aadhaar e-KYC", v: `${overview.aadhaar_kyc_pct}%` },
  ];

  const tabs = [
    { v: "overview", l: "Overview", I: ShieldCheck },
    { v: "apaar", l: "APAAR ID", I: IdentificationCard },
    { v: "kyc", l: "Aadhaar e-KYC", I: Lock },
    { v: "digilocker", l: "DigiLocker", I: Wallet },
    { v: "consent", l: "Consent", I: FileText },
  ];

  return (
    <div>
      <PageHeader overline="M03 · Identity & Onboarding" title={t("modules.identity.title")} description={t("modules.identity.subtitle")} />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {kpis.map(it => (
            <div key={it.label} className="bg-white p-5">
              <it.I size={20} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              <div className="eos-overline mt-3">{it.label}</div>
              <div className="font-heading font-extrabold text-2xl mt-1 tracking-tight">{it.v}</div>
            </div>
          ))}
        </div>

        <div className="inline-flex flex-wrap border border-border rounded-md overflow-hidden" data-testid="identity-tabs">
          {tabs.map(it => (
            <button key={it.v} onClick={() => setTab(it.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 border-r border-border last:border-r-0 ${tab === it.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`identity-tab-${it.v}`}>
              <it.I size={14}/> {it.l}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="eos-card" data-testid="identity-overview">
            <div className="font-heading font-bold text-base">Bulk operations</div>
            <div className="mt-3 space-y-2">
              {[
                { id: "issue_apaar", label: "Issue APAAR IDs to pending students" },
                { id: "verify_digilocker", label: "Verify DigiLocker for new intake" },
                { id: "push_abc", label: "Push completed credits to ABC" },
              ].map(a => (
                <button key={a.id} onClick={() => run(a.id)} disabled={busy === a.id}
                  className="w-full text-left p-3 border border-border rounded-md hover:bg-secondary disabled:opacity-50"
                  data-testid={`identity-action-${a.id}`}>
                  {busy === a.id ? "Running…" : a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "apaar" && (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="apaar-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Issue new APAAR ID</div>
              {[["student_id", "Student ID"], ["name", "Full name"], ["dob", "DOB (YYYY-MM-DD)"], ["aadhaar_last4", "Aadhaar last-4"]].map(([k, l]) => (
                <div key={k} className="mb-2">
                  <label className="eos-overline">{l}</label>
                  <input value={apaarForm[k]} onChange={(e) => setApaarForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`apaar-${k}`}/>
                </div>
              ))}
              <Button onClick={issueApaar} className="w-full bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)] mt-2" data-testid="issue-apaar-btn">Issue APAAR ID</Button>
            </div>
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Recent issuances ({apaarList.length})</div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {apaarList.length === 0 && <div className="text-sm text-muted-foreground italic">None yet.</div>}
                {apaarList.map(a => (
                  <div key={a.apaar_id} className="p-2.5 border border-border rounded-md text-[12px]" data-testid={`apaar-row-${a.apaar_id}`}>
                    <div className="flex items-center justify-between"><span className="font-mono-eos">{a.apaar_id}</span><span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{a.status}</span></div>
                    <div className="text-muted-foreground mt-0.5">{a.name} · {a.student_id}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "kyc" && (
          <div className="eos-card max-w-2xl" data-testid="kyc-pane">
            <div className="font-heading font-bold text-base mb-3">Aadhaar e-KYC (mocked)</div>
            <div className="text-[11px] text-muted-foreground mb-3">Step 1: enter aadhaar last-4 + grant consent → OTP sent. Step 2: enter OTP <code className="bg-secondary px-1 rounded">123456</code> to verify.</div>
            {!kyc.txn_id ? (
              <div className="space-y-2">
                <div><label className="eos-overline">Student ID</label><input value={kyc.student_id} onChange={(e) => setKyc(k => ({ ...k, student_id: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="kyc-student-id"/></div>
                <div><label className="eos-overline">Aadhaar last-4</label><input value={kyc.aadhaar_last4} onChange={(e) => setKyc(k => ({ ...k, aadhaar_last4: e.target.value }))} maxLength={4} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md font-mono-eos" data-testid="kyc-aadhaar"/></div>
                <label className="flex items-center gap-2 mt-2 text-[12px]"><input type="checkbox" checked={kyc.consent} onChange={(e) => setKyc(k => ({ ...k, consent: e.target.checked }))} data-testid="kyc-consent"/> I consent to share Aadhaar data for KYC verification (DPDP Act)</label>
                <Button onClick={startKyc} disabled={!kyc.consent} className="w-full bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)] mt-2" data-testid="kyc-start-btn">Send OTP</Button>
              </div>
            ) : kyc.status !== "verified" ? (
              <div className="space-y-2">
                <div className="text-[12px] text-emerald-700">OTP sent · txn <code className="font-mono-eos">{kyc.txn_id}</code></div>
                <div><label className="eos-overline">OTP</label><input value={kyc.otp} onChange={(e) => setKyc(k => ({ ...k, otp: e.target.value }))} maxLength={6} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md font-mono-eos text-center text-lg" data-testid="kyc-otp"/></div>
                <Button onClick={verifyKyc} className="w-full bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="kyc-verify-btn">Verify OTP</Button>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-md text-emerald-900" data-testid="kyc-verified">
                <div className="flex items-center gap-2"><Check size={18} weight="bold"/><span className="font-bold">KYC Verified successfully</span></div>
                <div className="text-[11px] mt-1">Student {kyc.student_id} · Aadhaar ****{kyc.aadhaar_last4}</div>
                <Button onClick={() => setKyc({ student_id: "", aadhaar_last4: "", consent: false, txn_id: "", otp: "", status: "" })} variant="outline" className="mt-3 h-7 text-[11px]" data-testid="kyc-reset-btn">Start new KYC</Button>
              </div>
            )}
          </div>
        )}

        {tab === "digilocker" && (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="digilocker-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Pull from DigiLocker</div>
              <div><label className="eos-overline">Student ID</label><input value={dlForm.student_id} onChange={(e) => setDlForm(f => ({ ...f, student_id: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="dl-student-id"/></div>
              <div className="mt-2"><label className="eos-overline">Documents (csv)</label><input value={dlForm.documents} onChange={(e) => setDlForm(f => ({ ...f, documents: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md font-mono-eos" data-testid="dl-docs"/></div>
              <Button onClick={pullDigiLocker} className="w-full bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)] mt-3" data-testid="dl-pull-btn"><Wallet size={14} className="mr-1.5"/> Pull documents</Button>
            </div>
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Pulled documents</div>
              {docs.length === 0 ? <div className="text-sm text-muted-foreground italic">None yet for this student.</div> : (
                <ul className="space-y-1.5 text-[12px]">
                  {docs.map(d => (
                    <li key={d.doc_id} className="flex items-center justify-between p-2 border border-border rounded" data-testid={`doc-${d.doc_id}`}>
                      <span className="flex items-center gap-2"><Check size={12} className="text-emerald-600"/>{d.doc_type.replace(/_/g, " ")}</span>
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">verified</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "consent" && (
          <div className="eos-card" data-testid="consent-pane">
            <div className="font-heading font-bold text-base mb-3">Consent ledger (DPDP)</div>
            <div className="flex items-end gap-2 mb-4">
              <div className="flex-1"><label className="eos-overline">Student ID</label><input value={dlForm.student_id} onChange={(e) => setDlForm(f => ({ ...f, student_id: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="consent-student-id"/></div>
              <Button onClick={loadConsents} className="bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="load-consents-btn">Load</Button>
            </div>
            <div className="space-y-2">
              {consents.length === 0 && <div className="text-sm text-muted-foreground italic">No consents found. Use APAAR / KYC / DigiLocker first to populate.</div>}
              {consents.map(c => (
                <div key={c.consent_id} className="flex items-start justify-between p-3 border border-border rounded-md" data-testid={`consent-${c.consent_id}`}>
                  <div>
                    <div className="font-mono-eos text-[11px] text-muted-foreground">{c.consent_id}</div>
                    <div className="font-medium text-sm">{c.scope.replace(/_/g, " ")}</div>
                    <div className="text-[11px] text-muted-foreground">{new Date(c.at).toLocaleString()} · {c.actor}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.granted ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{c.granted ? "granted" : "withdrawn"}</span>
                    {c.granted && <Button variant="ghost" onClick={() => withdrawConsent(c.consent_id)} className="h-6 w-6 p-0 text-rose-600" data-testid={`withdraw-${c.consent_id}`}><Trash size={12}/></Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
